import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth, effectiveTier, isPremiumOrOwner } from "@/lib/AuthContext";
import DashboardShell from "@/components/layout/SmartShell";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Key, ExternalLink, Trash2, Lock, Plus, Shield, CheckCircle2, AlertTriangle, Zap } from "lucide-react";
import ByokSetupChecklist from "@/components/byok/ByokSetupChecklist";

export default function ApiKeysPage() {
  const { user, loading } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [keys, setKeys] = useState([]);
  const [usage, setUsage] = useState(null);
  const [budget, setBudget] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [addOpen, setAddOpen] = useState(null); // provider id when adding
  const [confirmDelete, setConfirmDelete] = useState(null); // provider id pending delete

  // form state
  const [secret, setSecret] = useState("");
  const [label, setLabel] = useState("");

  useEffect(() => {
    api.get("/keys/catalog").then(({ data }) => setCatalog(data.providers || [])).catch(() => {});
  }, []);

  const refreshKeys = async () => {
    try { const { data } = await api.get("/keys/me"); setKeys(data.keys || []); }
    catch (e) {
      if (e.response?.status === 402) setKeys(null); // locked
    }
  };
  const refreshUsage = async () => {
    try { const { data } = await api.get("/health/usage/me"); setUsage(data); } catch {}
    try { const { data } = await api.get("/health/budget"); setBudget(data); } catch {}
  };

  useEffect(() => {
    if (isPremiumOrOwner(user)) {
      refreshKeys();
      refreshUsage();
    } else {
      setKeys(null);
    }
  }, [user]);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login?next=/settings/api-keys" replace />;

  const tier = effectiveTier(user);
  const isPremium = isPremiumOrOwner(user);

  const onSave = async (providerId) => {
    setMsg(null); setBusy(providerId);
    try {
      await api.put("/keys/me", { provider: providerId, secret, label });
      setSecret(""); setLabel(""); setAddOpen(null);
      await refreshKeys();
      setMsg({ kind: "success", text: `Saved your ${providerId} key — future ${providerId} generations will bill your account.` });
    } catch (e) {
      setMsg({ kind: "error", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(null); }
  };

  const onDelete = async (providerId) => {
    setBusy(providerId);
    try {
      await api.delete(`/keys/me/${providerId}`);
      await refreshKeys();
      setMsg({ kind: "success", text: `${providerId} key removed.` });
    } catch (e) {
      setMsg({ kind: "error", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(null); setConfirmDelete(null); }
  };

  const savedMap = Object.fromEntries((keys || []).map(k => [k.provider, k]));

  return (
    <DashboardShell>
      <div className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-4">Provider Keys</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Bring your own API keys</h1>
        <p className="text-[#A0A0A0] mt-4 max-w-2xl">
          Premium subscribers can route generations through their own OpenAI / Google / Anthropic / fal.ai / ElevenLabs / Suno accounts.
          Your secrets are encrypted at rest; we never display them after save. Generations fall back to platform keys if a provider is missing.
        </p>
      </div>

      {!isPremium && (
        <LockedBanner />
      )}

      {isPremium && <ByokSetupChecklist keys={keys || []} />}

      {msg && (
        <div
          data-testid="keys-msg"
          className={`mb-6 px-4 py-3 border text-sm flex items-start gap-3 ${
            msg.kind === "success"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-red-500/40 bg-red-500/10 text-red-300"
          }`}
        >
          <CheckCircle2 className="w-4 h-4 mt-0.5" /> {msg.text}
        </div>
      )}

      {isPremium && budget && <CapacityCard budget={budget} />}

      {isPremium && usage && (
        <UsageSummary usage={usage} />
      )}

      {isPremium && usage?.recent?.length > 0 && (
        <RecentCalls recent={usage.recent} />
      )}

      {isPremium && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {catalog.map((p) => {
            const saved = savedMap[p.id];
            const open = addOpen === p.id;
            return (
              <div key={p.id} data-testid={`provider-card-${p.id}`}
                   className={`border p-5 transition-colors ${
                     saved ? "border-emerald-500/40 bg-emerald-500/5" : "border-[#2A2A2A] bg-[#121212]"
                   }`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-display text-xl font-bold text-[#F4F4F0]">{p.label}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mt-1">
                      {p.tasks.join(" · ")}
                    </div>
                  </div>
                  {saved && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300">
                      <Shield className="w-3 h-3" /> Active
                    </span>
                  )}
                </div>

                {saved ? (
                  <div className="space-y-2">
                    <p className="text-xs text-[#A0A0A0]">
                      Key: <span className="font-mono text-[#F4F4F0]">{saved.preview}</span>
                    </p>
                    {saved.label && <p className="text-xs text-[#A0A0A0]">Label: {saved.label}</p>}
                    <p className="text-[10px] text-[#5A5A5A]">
                      Added {(saved.created_at || "").slice(0, 10)}
                      {saved.last_used_at && ` · last used ${(saved.last_used_at || "").slice(0, 10)}`}
                    </p>
                    {confirmDelete === p.id ? (
                      <div className="mt-3 p-3 border border-red-500/40 bg-red-500/10" data-testid={`confirm-delete-${p.id}`}>
                        <p className="text-xs text-red-200 mb-3">Remove this key? Future {p.label} generations will fall back to platform keys.</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => onDelete(p.id)}
                            disabled={busy === p.id}
                            data-testid={`confirm-delete-yes-${p.id}`}
                            className="bg-red-500/80 hover:bg-red-500 text-white text-xs font-bold px-3 py-1.5 transition-colors disabled:opacity-50"
                          >
                            Yes, remove
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            data-testid={`confirm-delete-no-${p.id}`}
                            className="text-xs text-[#A0A0A0] hover:text-[#F4F4F0] px-3 py-1.5"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDelete(p.id)}
                        data-testid={`delete-key-${p.id}`}
                        className="mt-3 inline-flex items-center gap-2 text-xs text-red-300 hover:text-red-200 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    )}
                  </div>
                ) : open ? (
                  <div className="space-y-3">
                    <input
                      type="password"
                      value={secret}
                      onChange={(e) => setSecret(e.target.value)}
                      placeholder={`Paste your ${p.label} secret key`}
                      data-testid={`key-secret-${p.id}`}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm font-mono text-[#F4F4F0] placeholder-[#5A5A5A]"
                    />
                    <input
                      type="text"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="Label (optional, e.g. YBM Inc Production)"
                      data-testid={`key-label-${p.id}`}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-xs text-[#F4F4F0] placeholder-[#5A5A5A]"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => onSave(p.id)}
                        disabled={busy === p.id || secret.length < 8}
                        data-testid={`save-key-${p.id}`}
                        className="bg-[#D4AF37] text-[#080808] font-bold px-4 py-2 text-xs hover:bg-[#F0C84B] transition-colors disabled:opacity-50"
                      >
                        Save & Encrypt
                      </button>
                      <button
                        onClick={() => { setAddOpen(null); setSecret(""); setLabel(""); }}
                        className="px-4 py-2 text-xs text-[#A0A0A0] hover:text-[#F4F4F0]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-[#A0A0A0] mb-3">
                      Get your key at{" "}
                      <a
                        href={`https://${p.where_to_get}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#D4AF37] hover:underline inline-flex items-center gap-1"
                      >
                        {p.where_to_get} <ExternalLink className="w-3 h-3" />
                      </a>
                    </p>
                    <button
                      onClick={() => { setAddOpen(p.id); setSecret(""); setLabel(""); }}
                      data-testid={`add-key-${p.id}`}
                      className="inline-flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] px-3 py-2 text-xs transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Key
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-12 border border-[#2A2A2A] bg-[#0F0F0F] p-6 text-xs text-[#A0A0A0]">
        <div className="flex items-center gap-2 mb-2 text-[#D4AF37] font-bold uppercase tracking-wider">
          <Lock className="w-3.5 h-3.5" /> Security
        </div>
        Keys are encrypted with AES-128-GCM (Fernet) before they touch the database.
        The plaintext value never leaves the router — once saved, only a 4-and-4 preview is shown.
      </div>
    </DashboardShell>
  );
}

function UsageSummary({ usage }) {
  const total = usage.total_cost_usd ?? 0;
  const bySrc = usage.by_key_source || {};
  return (
    <div className="mb-8 border border-[#2A2A2A] bg-[#121212] p-6" data-testid="usage-summary">
      <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0] mb-4">Last 30 days · est. spend</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Stat label="Your spend total" value={`$${total.toFixed(2)}`} highlight />
        <Stat label="On your keys" value={`$${(bySrc.user ?? 0).toFixed(2)}`} />
        <Stat label="On YBM platform" value={`$${(bySrc.platform ?? 0).toFixed(2)}`} />
        <Stat label="Via Universal Key" value={`$${(bySrc.emergent ?? 0).toFixed(2)}`} />
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">{label}</div>
      <div className={`font-display text-2xl font-extrabold ${highlight ? "text-[#D4AF37]" : "text-[#F4F4F0]"}`}>{value}</div>
    </div>
  );
}

function CapacityCard({ budget }) {
  const pct = Math.min(budget.percent_used || 0, 100);
  const isLocked = budget.status === "locked";
  const isWarn   = budget.status === "warning";
  const barColor =
    isLocked ? "bg-red-500" :
    isWarn   ? "bg-amber-500" :
               "bg-emerald-500";
  const cardColor =
    isLocked ? "border-red-500/40 bg-red-500/5" :
    isWarn   ? "border-amber-500/40 bg-amber-500/5" :
               "border-[#2A2A2A] bg-[#121212]";
  return (
    <div className={`mb-6 border p-6 ${cardColor}`} data-testid="capacity-card">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0] mb-1">Emergent Universal Key · backup capacity</p>
          <p className="text-[10px] text-[#5A5A5A]">
            Tracked estimate based on YBM's per-call logs.
            Authoritative balance lives at Profile → Universal Key on the Emergent platform.
          </p>
        </div>
        <div className="text-right">
          <span className="font-display text-3xl font-extrabold text-[#F4F4F0]">
            ${budget.spent_usd.toFixed(2)}
          </span>
          <span className="text-sm text-[#A0A0A0] ml-2">/ ${budget.budget_usd.toFixed(2)} this month</span>
        </div>
      </div>

      <div className="w-full h-2 bg-[#0A0A0A] border border-[#2A2A2A] overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${pct}%` }}
          data-testid="capacity-progress"
        />
      </div>
      <div className="flex justify-between text-[10px] text-[#5A5A5A] mt-1 uppercase tracking-wider">
        <span>{pct.toFixed(1)}% used</span>
        <span>Warn @ {budget.warn_pct}% · Soft-lock @ {budget.soft_lock_pct}%</span>
      </div>

      {isWarn && (
        <div className="mt-4 flex items-start gap-3 px-3 py-2 border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs" data-testid="capacity-warn">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Universal Key usage is above {budget.warn_pct}%. Top up at Profile → Universal Key or add a personal/platform key below to keep generations flowing.</span>
        </div>
      )}
      {isLocked && (
        <div className="mt-4 flex items-start gap-3 px-3 py-2 border border-red-500/40 bg-red-500/10 text-red-200 text-xs" data-testid="capacity-locked">
          <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span><b>Soft-locked.</b> Sora 2 video calls are paused until the Universal Key is topped up or a personal/platform key is configured. Image and voice still work.</span>
        </div>
      )}
    </div>
  );
}

function RecentCalls({ recent }) {
  return (
    <div className="mb-8 border border-[#2A2A2A] bg-[#0F0F0F]" data-testid="recent-calls">
      <p className="px-5 py-3 text-xs uppercase tracking-[0.22em] text-[#A0A0A0] border-b border-[#2A2A2A]">
        Recent generations · last 20
      </p>
      <div className="divide-y divide-[#1A1A1A]">
        {recent.map((r, i) => <CallRow key={i} r={r} />)}
      </div>
    </div>
  );
}

function CallRow({ r }) {
  const sourceBadge = {
    user:     { label: "Your Key",      color: "text-emerald-300 border-emerald-500/40" },
    platform: { label: "YBM Platform",  color: "text-[#D4AF37] border-[#D4AF37]/40"    },
    emergent: { label: "Universal Key", color: "text-blue-300 border-blue-500/40"      },
  }[r.key_source] || { label: r.key_source, color: "text-[#A0A0A0] border-[#2A2A2A]" };
  const detail = formatDetail(r);
  return (
    <div className="px-5 py-3 flex flex-wrap items-center gap-3 text-xs">
      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border ${sourceBadge.color}`}>
        {sourceBadge.label}
      </span>
      <span className="text-[#A0A0A0] uppercase tracking-wider text-[10px]">{r.feature}</span>
      <span className="font-mono text-[#F4F4F0]">{r.model}</span>
      <span className="text-[#A0A0A0] flex-1 truncate">{detail}</span>
      <span className={`font-mono text-xs ${r.success ? "text-[#F4F4F0]" : "text-red-300"}`}>
        ${(r.cost_usd || 0).toFixed(4)}
      </span>
      <span className="text-[10px] text-[#5A5A5A]">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
      {!r.success && <span className="text-[10px] text-red-300">FAIL</span>}
    </div>
  );
}

function formatDetail(r) {
  const e = r.extra || {};
  if (r.feature === "video") return `${e.duration ?? "?"}s · ${e.aspect ?? "?"}${e.scope === "owner_studio" ? " · admin" : ""}`;
  if (r.feature === "audio") return `${e.voice ?? ""}${e.scope === "owner_studio" ? " · admin" : ""}`;
  if (r.feature === "image") return `${e.aspect ?? ""}${e.scope === "owner_studio" ? " · admin" : ""}`;
  return "";
}

function LockedBanner() {
  return (
    <div
      data-testid="keys-locked-banner"
      className="mb-10 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5"
    >
      <Key className="w-7 h-7 text-[#D4AF37] flex-shrink-0" />
      <div className="flex-1">
        <p className="font-display text-xl font-bold text-[#F4F4F0] mb-1">BYO keys is a Premium feature</p>
        <p className="text-sm text-[#A0A0A0]">Upgrade to Premium to route generations through your own provider accounts and bypass the platform's pooled budget.</p>
      </div>
      <Link
        to="/pricing"
        data-testid="keys-upgrade-cta"
        className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#080808] font-bold px-5 py-3 text-sm hover:bg-[#F0C84B] transition-colors whitespace-nowrap"
      >
        See Premium
      </Link>
    </div>
  );
}
